/**
 * API service for handling all backend requests
 */
import { loadStripe } from '@stripe/stripe-js';
import { API_BASE_URL, REACT_APP_RECAPTCHA_SITE_KEY } from '../config';

const stripePromise = loadStripe('pk_test_51REia8CTdK10dM9bFdJ7nhhx7TdrUdzcq00RJC9e1UGTVk6rvDGJIr3tA1DYRPWAGTZlp7VXpaMevldYEpfa4Y0C00cHVfqc49');

// Base API URL - could be set from environment variables

/**
 * Check if username and email are unique
 * @param {string} username - The username to check
 * @param {string} email - The email to check
 * @returns {Promise<Object>} - Object with validation results
 */
export async function checkUniqueFields(username, email) {
  try {    
    // Make sure API_BASE_URL is defined
    if (!API_BASE_URL) {
      console.error('API_BASE_URL is not defined');
      return { 
        valid: false, 
        errors: { general: 'API configuration error' } 
      };
    }
    
    const url = `${API_BASE_URL}/api/lead_buyers/check-unique/`;
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ username, email })
    });
    
    
    if (response.ok) {
      const data = await response.json();
      return { valid: true };
    } else {
      const errorData = await response.json();
      return { valid: false, errors: errorData };
    }
  } catch (error) {
    console.error('Error checking unique fields:', error);
    return { 
      valid: false, 
      errors: { general: 'Network error while checking username and email' } 
    };
  }
}

/**
 * Fetch authenticated LeadBuyer profile (including allowed_domains)
 * @returns {Promise<Object>} LeadBuyer profile
 */
export async function getLeadBuyerProfile() {
  const token = localStorage.getItem('authToken');
  if (!token) throw new Error('Not authenticated');
  const response = await authFetch(`${API_BASE_URL}/api/lead_buyers/me/`, {
    method: 'GET',
  });
  if (!response.ok) throw new Error('Failed to fetch LeadBuyer profile');
  return response.json();
}

/**
 * Check if the authenticated user is an affiliate user (registered via affiliate link)
 * @returns {Promise<boolean>} True if user is an affiliate user
 */
export async function isAffiliateUser() {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) return false;
    const profile = await getLeadBuyerProfile();
    return profile.is_affiliate_user === true;
  } catch (error) {
    console.error('Error checking affiliate user status:', error);
    return false;
  }
}

/**
 * Example login function that stores access/refresh tokens in localStorage
 * @param {string} email
 * @param {string} password
 */
export async function loginAndStoreTokens(email, password) {
  const response = await fetch(`${API_BASE_URL}/api/token/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify({ email, password })
  });
  if (!response.ok) throw new Error('Login failed');
  const data = await response.json();
  // Beklenen: { access: ..., refresh: ... }
  if (data.access) localStorage.setItem('access', data.access);
  if (data.refresh) localStorage.setItem('refresh', data.refresh);
  return data;
}

// Utility to load Google reCAPTCHA v3 script
export function loadRecaptchaScript(REACT_APP_RECAPTCHA_SITE_KEY) {
  // Prevent duplicate script loads
  if (!REACT_APP_RECAPTCHA_SITE_KEY) {
    return Promise.reject(new Error('reCAPTCHA site key is missing!'));
  }
  return new Promise((resolve, reject) => {
    if (window.grecaptcha) return resolve();
    const existingScript = document.querySelector('script[src*="recaptcha/api.js"]');
    if (existingScript) {
      // If script exists but grecaptcha is not ready, wait for it
      existingScript.addEventListener('load', resolve);
      existingScript.addEventListener('error', () => reject(new Error('reCAPTCHA script could not be loaded. Please refresh the pagei')));
      return;
    }
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${REACT_APP_RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.onload = resolve;
    script.onerror = () => reject(new Error('reCAPTCHA script could not be loaded. Please refresh the page'));
    document.body.appendChild(script);
  });
}


/**
 * Request password reset
 * @param {string} email
 * @returns {Promise}
 */
export async function requestPasswordReset(email) {
  const response = await fetch(`${API_BASE_URL}/account/password-reset/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify({ email })
  });
  
  if (!response.ok) {
    const errorData = await response.json();
    const error = new Error(errorData.error || 'Failed to request password reset');
    error.response = { data: errorData };
    throw error;
  }
  
  return response.json();
}

/**
 * Checkout to Stripe (stub)
 */
export const checkoutToStripe = async (leadId, status, leadBuyerId) => {
  try {
    const stripe = await stripePromise;

    const response = await fetch(`${API_BASE_URL}/api/checkout/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({leadId, status, leadBuyerId})
    });

    // Eğer backend hata dönerse (ör. 400), hata mesajını göster
    if (!response.ok) {
      let errorMsg = 'Checkout unsuccessful, please try again.';
      try {
        const errorData = await response.json();
        if (errorData?.error) errorMsg = errorData.error;
        else if (typeof errorData === 'string') errorMsg = errorData;
      } catch (_) {}
      alert(errorMsg);
      return;
    }

    const session = await response.json();

    const result = await stripe.redirectToCheckout({
      sessionId: session.id,
    });

    if (result.error) {
      alert(result.error.message);
    }
    // return data;
  } catch (error) {
    console.error('Error submitting lead data:', error);
    
    // If it's already a structured error with response, rethrow it
    if (error.response) {
      throw error;
    }
    
    // Otherwise, create a structured error
    const structuredError = new Error(error.message || 'Network error');
    structuredError.response = {
      status: 0,
      data: { error: error.message || 'Network error occurred' }
    };
    throw structuredError;
  }
};

/**
 * Update lead status (stub)
 */
export async function updateLeadStatus(leadId, status) {
  // TODO: Implement actual logic or connect to backend
  throw new Error('updateLeadStatus not implemented');
}

// Fetch a specific lead by ID
export async function fetchLead(leadId) {
  const response = await authFetch(`${API_BASE_URL}/api/leadbuyerleads/${leadId}/`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch lead');
  }

  return await response.json();
}

// Fetch notes for a lead
export async function fetchLeadNotes(leadId) {
  const response = await authFetch(`${API_BASE_URL}/api/leadbuyerleads/${leadId}/notes/`, {
    method: 'GET',
    headers: {
      'X-Session-ID': sessionStorage.getItem('session_id') || ''
    }
  });
  if (!response.ok) throw new Error("Failed to fetch notes");
  return await response.json();
}

// Add a note to a lead
export async function addLeadNote(leadId, content) {
  const response = await authFetch(`${API_BASE_URL}/api/leadbuyerleads/${leadId}/notes/`, {
    method: 'POST',
    headers: {
      'X-Session-ID': sessionStorage.getItem('session_id') || ''
    },
    body: JSON.stringify({ content }),
  });
  if (!response.ok) throw new Error("Failed to add note");
  return await response.json();
}

/**
 * Check the payment/offer status of a lead for the current buyer
 * @param {string} leadId
 * @param {string|number} leadBuyerId
 * @returns {Promise<{status: string, offer_status: string, message?: string}>}
 */
export async function checkLeadPaymentStatus(leadId, leadBuyerId) {
  const token = localStorage.getItem('authToken');
  const response = await fetch(`${API_BASE_URL}/api/leads/${leadId}/offer-status/?lead_buyer_id=${leadBuyerId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    }
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || 'Could not verify payment/offer status');
  }
  return response.json();
}


/**
 * Submit lead data to the backend
 * @param {Object} data - The lead data to submit
 * @returns {Promise} - The fetch promise
 */
export const submitLeadData = async (leadData) => {
  try {
    // Guarantee session_id exists
    let sessionId = sessionStorage.getItem('session_id');
    if (!sessionId) {
      sessionId = crypto.randomUUID();
      sessionStorage.setItem('session_id', sessionId);
    }
    const response = await fetch(`${API_BASE_URL}/api/leads/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Session-ID': sessionId
      },
      body: JSON.stringify({ ...leadData, session_id: sessionId })
    });
    
    // Always try to parse the response body
    const data = await response.json().catch(() => ({}));
    
    // If the response is not successful, throw an error with the response details
    if (!response.ok) {
      const error = new Error(data.error || `Error ${response.status}: ${response.statusText}`);
      error.response = {
        status: response.status,
        data: data
      };
      // throw error;
    }
    
    return data;
  } catch (error) {
    console.error('Error submitting lead data:', error);
    
    // If it's already a structured error with response, rethrow it
    if (error.response) {
      throw error;
    }
    
    // Otherwise, create a structured error
    const structuredError = new Error(error.message || 'Network error');
    structuredError.response = {
      status: 0,
      data: { error: error.message || 'Network error occurred' }
    };
    throw structuredError;
  }
};

/**
 * Get all leads from the backend
 * @returns {Promise} - The fetch promise with leads data
 */
// Utility to throw auth error if response is 401/403
function handleAuthError(response, errorData) {
  if (response.status === 401 || response.status === 403) {
    const error = new Error(errorData.error || 'Authentication error');
    error.isAuth = true;
    throw error;
  }
}

export const getLeads = async () => {
  try {
    const response = await authFetch(`${API_BASE_URL}/api/leadbuyerleads/`, {
      method: 'GET',
      headers: {
        'X-Session-ID': sessionStorage.getItem('session_id') || ''
      }
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      handleAuthError(response, errorData);
      throw new Error(errorData.error || `Error ${response.status}: ${response.statusText}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching leads:', error);
    throw error;
  }
};


/**
 * Get nearby leads based on zip code
 * @param {string} zipCode - The zip code to search for nearby leads
 * @returns {Promise} - The fetch promise with nearby leads data
 */
export const getNearbyLeads = async (zipCode, country) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/api/lead_buyers/nearby/?zip_code=${zipCode}&country=${country}`, {
      method: 'GET',
      headers: {
        'X-Session-ID': sessionStorage.getItem('session_id') || ''
      }
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      handleAuthError(response, errorData);
      throw new Error(errorData.error || `Error ${response.status}: ${response.statusText}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`Error fetching nearby leads for zip code ${zipCode}:`, error);
    throw error;
  }
};


/**
 * Login a user
 * @param {Object} credentials - User credentials (username and password)
 * @returns {Promise} - The fetch promise with auth token
 */
export const loginUser = async (credentials) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/lead_buyers/login/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(credentials)
    });
    let data = null;
    try {
      data = await response.json();
    } catch (_) {
      data = {};
    }
    if (!response.ok) {
      const error = new Error((data && data.error) || `Error ${response.status}: ${response.statusText}`);
      error.response = {
        status: response.status,
        data: data
      };
      throw error;
    }
    // Tokenları kaydet
    if (data && data.access) localStorage.setItem('authToken', data.access);
    if (data && data.refresh) localStorage.setItem('refreshToken', data.refresh);
    return data || {};
  } catch (error) {
    if (!error.response) {
      console.error('Login error:', error);
    }
    throw error;
  }
};


/**
 * Register a new user
 * @param {Object} userData - User registration data
 * @returns {Promise} - The fetch promise with registration response
 */
export const registerUser = async (userData) => {
  try {
    // Always get reCAPTCHA token before registration
    await loadRecaptchaScript(REACT_APP_RECAPTCHA_SITE_KEY);
    let recaptchaToken;
    try {
      await new Promise(resolve => window.grecaptcha.ready(resolve));
      recaptchaToken = await window.grecaptcha.execute(REACT_APP_RECAPTCHA_SITE_KEY, { action: 'register' });
    } catch (err) {
      throw new Error('reCAPTCHA doğrulaması başarısız. Lütfen tekrar deneyin.');
    }
    
    // Create a clean payload with proper carried_products format
    const payload = { ...userData };
    
    // If recaptcha_token is not already in userData, add it
    if (!payload.recaptcha_token) {
      payload.recaptcha_token = recaptchaToken;
    }
    
    // Make sure carried_products is properly formatted
    if (payload.carried_products && typeof payload.carried_products === 'object') {
      // The backend expects carried_products as a JSON object
      // No need to stringify it as fetch will do that for the entire body
    }
    
   
    
    // Ensure disclaimer fields are explicitly included in the final payload
    const finalPayload = {
      ...payload,
      disclaimer_accepted: payload.disclaimer_accepted === true,
      disclaimer_accepted_at: payload.disclaimer_accepted_at || new Date().toISOString(),
      disclaimer_accepted_date: payload.disclaimer_accepted_date || new Date().toLocaleDateString(),
      disclaimer_accepted_time: payload.disclaimer_accepted_time || new Date().toLocaleTimeString()
    };
  
    const response = await fetch(`${API_BASE_URL}/api/lead_buyers/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(finalPayload)
    });
    const data = await response.json();
    if (!response.ok) {
      const error = new Error(data.error || `Error ${response.status}: ${response.statusText}`);
      error.response = {
        status: response.status,
        data: data
      };
      throw error;
    }
    // Tokenları kaydet
    localStorage.setItem('authToken', data.access);
    localStorage.setItem('refreshToken', data.refresh);
    return data;
  } catch (error) {
    throw error;
  }
};


/**
 * Verify auth token is valid
 * @returns {Promise} - The fetch promise with user data
 */
export const verifyToken = async () => {
  const response = await authFetch(`${API_BASE_URL}/api/auth/verify/`, {
    method: 'GET',
  });
  if (!response.ok) {
    throw new Error(`Error ${response.status}: ${response.statusText}`);
  }
  return await response.json();
};

// --- JWT Token Refresh & Interceptor ---
// Token refresh queue to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshPromise = null;

export const refreshAccessToken = async () => {
  // If a refresh is already in progress, wait for it
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }
  
  // Mark that we're refreshing and create the promise
  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        console.error('No refresh token found in localStorage');
        throw new Error('No refresh token found');
      }
      
      
      const response = await fetch(`${API_BASE_URL}/api/token/refresh/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh: refreshToken })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Token refresh failed:', {
          status: response.status,
          statusText: response.statusText,
          errorBody: errorText
        });
        throw new Error(`Refresh token error: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      if (!data.access) {
        console.error('Token refresh response missing access token:', data);
        throw new Error('Invalid token refresh response');
      }
      
      localStorage.setItem('authToken', data.access);
      return data.access;
    } catch (error) {
      console.error('Token refresh exception:', error);
      throw error;
    } finally {
      // Reset the refresh state
      isRefreshing = false;
      refreshPromise = null;
    }
  })();
  
  return refreshPromise;
};

/**
 * Expire a lead offer immediately (called by frontend timer)
 * @param {number|string} offerId
 * @returns {Promise<object>} backend response
 */
export async function expireLeadOffer(offerId) {
  const response = await authFetch(`${API_BASE_URL}/api/offers/${offerId}/expire/`, {
    method: 'POST',
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.reason || 'Failed to expire offer');
  return data;
}

// Enhanced authFetch: Handles JWT token expiration and auto-logout on refresh failure
export const authFetch = async (url, options = {}) => {
  let token = localStorage.getItem('authToken');
  
  // Check if body is FormData (for file uploads)
  const isFormData = options.body instanceof FormData;
  
  options.headers = {
    ...(options.headers || {}),
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    // Don't set Content-Type for FormData (browser will set it with boundary)
    ...(!isFormData && { 'Content-Type': 'application/json' }),
    'Accept': 'application/json',
    'X-Session-ID': sessionStorage.getItem('session_id') || ''
  };

  let response = await fetch(url, options);

  if (response.status === 401) {
    const refreshToken = localStorage.getItem('refreshToken');

    if (!refreshToken) {
      console.error('No refresh token available, cannot refresh session');
      return response; // Return the original 401 response
    }

    try {
      // Use the queued refresh mechanism to prevent multiple simultaneous refreshes
      const newToken = await refreshAccessToken();

      // Update localStorage with new token (critical for subsequent requests)
      localStorage.setItem('authToken', newToken);

      // Retry the original request with the new token
      options.headers['Authorization'] = `Bearer ${newToken}`;
      response = await fetch(url, options);

      if (response.ok) {
        console.log('Request succeeded after token refresh');
      } else {
        console.error('Request still failed after token refresh:', {
          status: response.status,
          statusText: response.statusText
        });
      }

      return response;

    } catch (err) {
      // Refresh failed: clear all auth and redirect to login
      console.error('Token refresh failed completely:', err);

      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('authUser');

      // Check if we're already on the login page to avoid redirect loops
      const isLoginPage = window.location.pathname.includes('/login');

      if (!isLoginPage) {
        // Instead of immediately redirecting, show an alert and then redirect
        alert('Your session has expired. You will be redirected to the login page.');

        // Small delay before redirect to ensure alert is seen
        setTimeout(() => {
          window.location.href = '/login';
        }, 1000);
      } else {
        console.error('Already on login page, not redirecting to avoid loop');
      }

      const sessionErr = new Error('Session expired. Please login again.');
      sessionErr.isAuth = true;
      throw sessionErr;
    }
  }

  return response;
}